import { ReadOutlined } from '@ant-design/icons'
import { useDebounce, useRequest } from 'ahooks'
import {
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Table,
  TablePaginationConfig,
  Typography,
} from 'antd'
import { isNil, omit } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'

import { Editor, MosaicComponent, RiskBar, UserAvatar, UsersSearch, getPointPermission } from '@/components'
import { HotResource } from '@/components/hot-resource/hot-resource.component'
import { CommitteeMemberManager } from '@/components/resource-committee-members'
import { routes } from '@/router/router.config'
import { PAGE_RESOURCE_POINTS_ENUM, PriorityEnum, ResourceType } from '@/schemas'
import { ga, traceEvent } from '@/utils/ga'

import { Header } from '../header'
import { classificationOptions } from './devlop-form.component'
import { DevlopHomeExport } from './devlop-home-export.component'
import { Devlop, DevlopClassification, DevlopStatus } from './devlop.schema'

// import type { ColumnType } from 'antd/es/table'

const { Text } = Typography
const { RangePicker } = DatePicker

export const DevlopHome: React.FC<{}> = () => {
  const { t } = useTranslation()

  const [selfIds, setSelfIds] = React.useState<string[]>([])

  React.useEffect(() => {
    ga('event', traceEvent.navigation.events.show_navigation, {
      event_category: traceEvent.navigation.event_categories.ef_pmo,
      event_label: traceEvent.navigation.event_labels.devlops,
      action: traceEvent.navigation.action,
    })
  }, [])

  return (
    <>
      <Header
        leftExtra={
          <div>
            <ReadOutlined />
            <Button
              className='pl-1'
              type='link'
              onClick={() =>
                window.open('https://confluence.agoralab.co/pages/viewpage.action?pageId=1271694179', '_blank')
              }
            >
              {t('devlop.document')}
            </Button>
          </div>
        }
        resourceType={ResourceType.DEVLOP}
        actions={[<DevlopHomeExport key='export' selfIds={selfIds} />]}
      />
      <div className='pt-2'>
        <CommitteeMemberManager resourceType={ResourceType.DEVLOP} />
      </div>
      <div className='pb-2'>
        <DevlopList withParamPersist={true} onSelfIdsChange={setSelfIds} />
      </div>
    </>
  )
}

export const DevlopList: React.FC<{
  canExport?: boolean
  isFullWidth?: boolean
  withParamPersist?: boolean
  extraOperationLoading?: boolean
  renderExtras?: any
  onSelfIdsChange?: (selfIds: string[]) => void
}> = ({
  withParamPersist = true,
  isFullWidth = true,
  extraOperationLoading = false,
  renderExtras,
  onSelfIdsChange,
}) => {
  const { t } = useTranslation()
  const history = useHistory()

  const defaultPageSize = 50
  const translateMap = {
    [DevlopStatus.未开始]: t('devlop.status.notStart'),
    [DevlopStatus.进行中]: t('devlop.status.ongoing'),
    [DevlopStatus.暂停]: t('devlop.status.pause'),
    // [DevlopStatus.终止]: t('devlop.status.termination'),
    [DevlopStatus.已结项]: t('devlop.status.finished.title'),
  }

  const [expandRows, setExpandRows] = React.useState<number[]>([])
  const [formValue, setFormValue] = React.useState<any>({})
  const [tableRowData, setTableRowData] = React.useState<Devlop[]>([])
  const [current, setCurrent] = React.useState<number>()
  const [pageSize, setPageSize] = React.useState(defaultPageSize)
  const [total, setTotal] = React.useState(0)
  const debouncedNameSearch = useDebounce(formValue.name, { wait: 500 })

  const searchFormRef = React.useRef<{
    form?: FormInstance<any>
    search: (query?: { [key: string]: any }) => void
  }>(null)

  const onFormChange = (formValue: any) => {
    persistSearch({
      page: formValue.page,
      pageSize: formValue.pageSize,
      formValue: omit(formValue, ['page', 'pageSize']),
    })
  }

  const { run: runQueryList, loading } = useRequest(
    ({ params }) => {
      return {
        url: '/api/devlop/list',
        method: 'get',
        params,
      }
    },
    {
      manual: true,
    }
  )

  const queryList = (params = {}) => {
    if (!Object.keys(params).length) return
    runQueryList({ params }).then(({ items, pagination: { total } }) => {
      setTotal(total)
      const remoteData = items.map((item: Devlop) => {
        return {
          ...item,
          key: item.selfId,
        }
      })
      setTableRowData(remoteData)
      onSelfIdsChange?.(remoteData.map((d: Devlop) => d.selfId))
    })
  }

  React.useEffect(() => {
    if (debouncedNameSearch === formValue.name) {
      queryList(formValue)
    }
  }, [debouncedNameSearch, formValue])

  const persistSearch = (
    params?: Partial<{
      page: number
      pageSize: number
      formValue: { orderKey?: string; order?: string }
    }>
  ) => {
    if (!withParamPersist) {
      setFormValue(params?.formValue)
      return
    }
    const { page: paramPage, pageSize: paramPageSize, formValue: paramFormValue } = params || {}
    const { page: _page, pageSize: _pageSize, ...value } = queryString.parse(location.search) as any
    const page = !isNil(paramPage) ? paramPage : !isNil(_page) ? _page : 0
    const pageSize = paramPageSize ?? _pageSize ?? defaultPageSize
    const formValue = { ...value, ...paramFormValue }

    const search = { ...formValue, page, pageSize } as any
    search.status = search.status ?? []
    history.replace({
      search: queryString.stringify(Object.assign(queryString.parse(location.search), search)),
    })
    setCurrent(Number(search.page) + 1)
    setPageSize(Number(search.pageSize))
    setFormValue(search)

    return search
  }

  const handleTableChange = ({ current, pageSize }: TablePaginationConfig, _filters: any, sorter?: any) => {
    if (pageSize) {
      setPageSize(pageSize)
    }
    if (current) {
      setCurrent(current)
    }
    const page = current ? current - 1 : 0
    const size = pageSize ? pageSize : defaultPageSize
    persistSearch({
      pageSize: size,
      page,
      formValue: { orderKey: sorter?.columnKey, order: sorter?.order },
    })
    // searchFormRef.current?.search(sortInfo)
  }

  const expandDetail = (record: Devlop & { key: number }) => {
    const key = record.key
    const index = expandRows.indexOf(key)
    if (~index) {
      setExpandRows(expandRows.filter((_, i) => i !== index))
    } else {
      setExpandRows([...expandRows, record.key])
    }
  }

  // const searchInput = useRef(null)

  // const getColumnSearchProps = (dataIndex: string): ColumnType<Devlop> => ({
  //   // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => (
  //   //   <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
  //   //     <Input
  //   //       ref={searchInput}
  //   //       placeholder={`Search ${dataIndex}`}
  //   //       value={selectedKeys[0]}
  //   //       onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
  //   //       onPressEnter={() => confirm()}
  //   //       style={{ marginBottom: 8, display: 'block' }}
  //   //     />
  //   //     <Space>
  //   //       <Button type='primary' onClick={() => confirm()} icon={<SearchOutlined />} size='small' style={{ width: 90 }}>
  //   //         Search
  //   //       </Button>
  //   //     </Space>
  //   //   </div>
  //   // ),
  //   // filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
  //   // onFilter: (value: any, record: any) =>
  //   //   !!record.roles.find((r: any) => r.role === dataIndex && r.email.indexOf(value) !== -1),
  //   render: (record: any) => {
  //     const email = record.roles.find((r: any) => r.role === dataIndex)?.email
  //     return (
  //       <Text className='flex-grow flex'>
  //         <UserAvatar showName email={email} />
  //       </Text>
  //     )
  //   },
  // })

  const columns = [
    {
      width: 150,
      title: t('devlop.owner'),
      dataIndex: 'email',
      key: 'email',
      render: (email: string, record: Devlop) => {
        return (
          <Text className='flex-grow flex'>
            <RiskBar className='mr-20px' risk={record.risk} />
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_负责人, record.permissions).canRead ? (
              <MosaicComponent />
            ) : (
              <UserAvatar showName email={email} />
            )}
          </Text>
        )
      },
    },
    {
      width: 400,
      ellipsis: true,
      title: t('devlop.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Devlop) => {
        return (
          <Link
            onClick={() => {
              window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_devlops)
            }}
            to={routes.devlop.detail(record.selfId)}
          >
            <HotResource selfId={record.selfId} type={record.type} readonly hotData={record.hots} />
            <span className='ml-4'>{name}</span>
          </Link>
        )
      },
    },
    {
      width: 120,
      title: t('devlop.detail.classification'),
      key: 'classification',
      sorter: true,
      render(record: any) {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_分类, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          t(DevlopClassification[record.classification])
        )
      },
    },
    {
      width: 250,
      title: t('devlop.timePeriod'),
      render: (_: any, record: Devlop) => {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          `${moment(record.startDate).format('YYYY-MM-DD')} - ${
            record.endDate ? moment(record.endDate).format('YYYY-MM-DD') : 'N/A'
          }`
        )
      },
    },
    {
      width: 140,
      title: t('devlop.detail.priority'),
      key: 'priority',
      sorter: true,
      render(record: any) {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          t(`form.priority.${PriorityEnum[record.priority]}`)
        )
      },
    },
    // {
    //   width: 120,
    //   title: t('devlop.to'),
    //   key: 'to',
    //   ...getColumnSearchProps('TO'),
    // },
    {
      width: 120,
      title: t('devlop.status.title'),
      key: 'status',
      sorter: true,
      render(record: any) {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          translateMap[record.status as keyof typeof translateMap]
        )
      },
    },
    {
      width: 120,
      title: t('devlop.actualWorkload.title'),
      key: 'actual_workload', // 字段被后端 order 所使用
      sorter: true,
      render(record: any) {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          Number(record.actualWorkload).toFixed(0)
        )
      },
    },
  ]

  if (isFullWidth) {
    columns.push({
      width: 200,
      title: t('devlop.operation.title'),
      dataIndex: '',
      key: 'x',
      render: (devlop: Devlop & { key: number }) => (
        <>
          {~expandRows.indexOf(devlop.key) ? (
            <Button onClick={() => expandDetail(devlop)} type='link' size='small'>
              {t('devlop.operation.close')}
            </Button>
          ) : (
            <Button onClick={() => expandDetail(devlop)} type='link' size='small'>
              {t('devlop.operation.open')}
            </Button>
          )}
        </>
      ),
    } as any)
  }

  if (renderExtras) {
    columns.push({
      align: 'center',
      width: 80,
      render: renderExtras,
    } as any)
  }

  const DevlopExtendDetail: React.FC<{ devlop: Devlop }> = ({ devlop }) => {
    return (
      <Descriptions bordered style={{ background: 'white' }} column={6}>
        <Descriptions.Item label={t('devlop.detail.goal')} span={6}>
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_交付目标, devlop.permissions).canRead}
            content={devlop.goal}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('devlop.detail.standard')} span={6}>
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], devlop.permissions).canRead}
            content={devlop.standard}
          />
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span>
              {t('devlop.detail.reports')}
              <span className='font-bold ml-2 text-red-500'>
                {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.周报_内容, devlop.permissions).canRead ? (
                  <MosaicComponent />
                ) : devlop.thisWeekReport ? (
                  <span>
                    ( {moment(devlop.thisWeekReport.startDate).format('YYYY-MM-DD')} -{' '}
                    {moment(devlop.thisWeekReport.endDate).format('YYYY-MM-DD')} )
                  </span>
                ) : null}
              </span>
            </span>
          }
          labelStyle={{ width: '22.22%' }}
          span={6}
        >
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.周报_内容, devlop.permissions).canRead}
            content={devlop.thisWeekReport?.extras?.find((e: any) => e.field === '当前进展')?.content}
          />
        </Descriptions.Item>
      </Descriptions>
    )
  }

  return (
    <div>
      <DevlopSearchForm ref={searchFormRef} onChange={onFormChange} />
      <Table
        className='overflow-x-hidden'
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        pagination={{
          showSizeChanger: true,
          total,
          pageSize,
          current,
          showTotal: (total) => t(`devlop.count`, { count: total }),
        }}
        columns={columns}
        loading={loading || extraOperationLoading}
        dataSource={tableRowData}
        expandable={{
          expandedRowKeys: expandRows,
          expandRowByClick: true,
          expandedRowRender: (devlop) => <DevlopExtendDetail devlop={devlop} />,
          expandIcon: () => null,
          columnWidth: 0,
        }}
      />
    </div>
  )
}

const searchType = [
  {
    label: 'all',
    value: 0,
  },
  {
    label: 'owner',
    value: 1,
  },
  {
    label: 'TO',
    value: 2,
  },
  {
    label: 'join in',
    value: 3,
  },
]

const DevlopSearchForm = React.forwardRef<
  any,
  {
    onChange: (value: any) => void
  }
>(({ onChange, ...props }, ref) => {
  const { t } = useTranslation()

  const translateMap = {
    [DevlopStatus.未开始]: t('devlop.status.notStart'),
    [DevlopStatus.进行中]: t('devlop.status.ongoing'),
    [DevlopStatus.暂停]: t('devlop.status.pause'),
    // [DevlopStatus.终止]: t('devlop.status.termination'),
    [DevlopStatus.已结项]: t('devlop.status.finished.title'),
  }

  const [form] = Form.useForm<any>()

  const search = (query: any = {}) => {
    const { page, pageSize } = queryString.parse(location.search)
    const { range, ...other } = form.getFieldsValue()
    const values = {
      page,
      pageSize,
      ...other,
      ...query,
      startDate: range ? range[0]?.format('YYYY-MM-DD') : '',
      endDate: range ? range[1]?.format('YYYY-MM-DD') : '',
    }

    return onChange(values)
  }

  React.useImperativeHandle(ref, () => ({
    search,
    form,
  }))

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, pageSize, ...value } = queryString.parse(location.search)

    let status: DevlopStatus[] | undefined = [DevlopStatus.未开始, DevlopStatus.进行中]
    if (!isNil(page)) {
      status = value.status
        ? Array.isArray(value.status)
          ? value.status.map((v) => Number(v))
          : [Number(value.status)]
        : undefined
    }

    const startDate = value.startDate ? moment(value.startDate) : undefined
    const endDate = value.endDate ? moment(value.endDate) : undefined
    const formData = {
      email: value.email || undefined,
      name: value.name || undefined,
      range: [startDate, endDate],
      searchType: value.searchType ? Number(value.searchType) : 1,
      classification: value.classification
        ? Array.isArray(value.classification)
          ? value.classification.map((v) => Number(v))
          : [Number(value.classification)]
        : undefined,
      // 当无 status 状态时将默认加入 default 状态
      status,
    }

    form.setFieldsValue(formData)

    const { range, ...other } = formData
    const values = {
      page,
      pageSize,
      ...other,
      startDate: range ? range[0]?.format('YYYY-MM-DD') : '',
      endDate: range ? range[1]?.format('YYYY-MM-DD') : '',
    }

    onChange(values)
  }, [])

  return (
    <div className='w-full py-4'>
      <Form {...props} form={form} name='devlopSearch' layout='inline' onFieldsChange={() => search({ page: 0 })}>
        <Row className='w-full' gutter={[{ xs: 4, sm: 8, md: 16, lg: 24 }, 16]}>
          <Col span={8}>
            <Form.Item name='name'>
              <Input placeholder={t('devlop.name')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Input.Group compact className='!flex'>
              <Form.Item name='email' className='!flex-1'>
                <UsersSearch placeholder={t('devlop.owner')} />
              </Form.Item>
              <Form.Item name='searchType' className='w-100px'>
                <Select
                  options={searchType.map((s) => {
                    return {
                      label: t(s.label),
                      value: s.value,
                    }
                  })}
                />
              </Form.Item>
            </Input.Group>
          </Col>
          <Col span={8}>
            <Form.Item name='classification'>
              <Select
                placeholder={t('devlop.detail.classification')}
                allowClear
                mode='multiple'
                options={classificationOptions}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row className='mt-10px w-full' gutter={[{ xs: 4, sm: 8, md: 16, lg: 24 }, 16]}>
          <Col span={8}>
            <Form.Item required name='range'>
              <RangePicker className='w-full' format='YYYY-MM-DD' />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name='status'>
              <Select
                placeholder={t('devlop.status.title')}
                allowClear
                mode='multiple'
                options={Object.keys(translateMap).map((c) => ({
                  value: Number(c),
                  label: translateMap[c as unknown as keyof typeof translateMap],
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  )
})
